// ---------------------------------------------------------------------------
// Form data
// ---------------------------------------------------------------------------

export interface RecordingAgreementFormData {
  artistName: string;
  labelCompany: string;
  advanceAmount: string;
  royaltySplit: number;
  territory: string;
  contractDuration: string;
  albumOptions: string;
}

export const INITIAL_FORM_DATA: RecordingAgreementFormData = {
  artistName: '',
  labelCompany: '',
  advanceAmount: '',
  royaltySplit: 0,
  territory: '',
  contractDuration: '',
  albumOptions: '',
};

// ---------------------------------------------------------------------------
// Form field definitions (right-side form display)
// ---------------------------------------------------------------------------

export interface FormFieldDef {
  key: keyof RecordingAgreementFormData;
  label: string;
  description: string;
  inputType: 'text' | 'currency' | 'select' | 'slider';
  placeholder?: string;
  options?: string[];
}

export const FORM_FIELDS: FormFieldDef[] = [
  {
    key: 'artistName',
    label: 'Artist / Band',
    description: 'The performing artist entering the agreement',
    inputType: 'text',
    placeholder: 'e.g., Jordan Rivera',
  },
  {
    key: 'labelCompany',
    label: 'Label / Company',
    description: 'The record label or entity on the other side',
    inputType: 'text',
    placeholder: 'e.g., Def Jam Recordings',
  },
  {
    key: 'advanceAmount',
    label: 'Advance',
    description: 'Recoupable advance paid upon signing',
    inputType: 'currency',
    placeholder: '150,000',
  },
  {
    key: 'royaltySplit',
    label: 'Royalty Split',
    description: "Artist's share of net royalties after recoupment",
    inputType: 'slider',
  },
  {
    key: 'territory',
    label: 'Territory',
    description: 'Geographic scope of the recording rights',
    inputType: 'select',
    options: [
      'Worldwide',
      'North America',
      'United States',
      'European Union',
      'United Kingdom',
      'Custom',
    ],
  },
  {
    key: 'contractDuration',
    label: 'Contract Duration',
    description: 'Length of the initial contract period',
    inputType: 'select',
    options: [
      '1 Year',
      '2 Years',
      '3 Years',
      '4 Years',
      '5 Years',
      '6 Years',
      '7 Years',
    ],
  },
  {
    key: 'albumOptions',
    label: 'Albums & Options',
    description: 'Firm albums and optional albums the label can exercise',
    inputType: 'select',
    options: [
      '1 Album, No Options',
      '1 Album + 1 Option',
      '1 Album + 2 Options',
      '2 Albums, No Options',
      '2 Albums + 1 Option',
      '2 Albums + 2 Options',
      '3 Albums, No Options',
    ],
  },
];

// ---------------------------------------------------------------------------
// Scripted conversation — auto-plays both sides
// ---------------------------------------------------------------------------

export interface ScriptLine {
  role: 'assistant' | 'user';
  text: string;
  /** Form fields to fill when this line appears */
  fills?: Partial<RecordingAgreementFormData>;
}

/**
 * Natural back-and-forth conversation. The "user" describes the deal,
 * the assistant acknowledges and fills in fields.
 */
export const CONVERSATION_SCRIPT: ScriptLine[] = [
  // --- Intro ---
  {
    role: 'assistant',
    text: "Hey! Let's put together a Recording Agreement. Tell me about the deal — who's involved and what are you thinking on terms?",
  },

  // --- User describes the deal broadly ---
  {
    role: 'user',
    text: "We're putting together a deal for an artist named Marcus Cole, signing with Def Jam. They've been negotiating for a few weeks now.",
    fills: {
      artistName: 'Marcus Cole',
      labelCompany: 'Def Jam Recordings',
    },
  },
  {
    role: 'assistant',
    text: "Got it — **Marcus Cole** signing with **Def Jam Recordings**. I've filled those in. What's the advance looking like?",
  },

  // --- Financial terms ---
  {
    role: 'user',
    text: "They landed on a $250K advance. The royalty split is 70/30 in the artist's favor after recoupment.",
    fills: {
      advanceAmount: '250,000',
      royaltySplit: 70,
    },
  },
  {
    role: 'assistant',
    text: "Nice — **$250,000** advance with a **70/30** split favoring the artist. Those are solid terms. What about territory and duration?",
  },

  // --- Territory & duration ---
  {
    role: 'user',
    text: "Worldwide rights, 3 year initial term.",
    fills: {
      territory: 'Worldwide',
      contractDuration: '3 Years',
    },
  },
  {
    role: 'assistant',
    text: "**Worldwide** territory, **3 year** term — locked in. Last thing: how many albums are committed, and does Def Jam have options for more?",
  },

  // --- Albums ---
  {
    role: 'user',
    text: "2 firm albums with an option for a third.",
    fills: {
      albumOptions: '2 Albums + 1 Option',
    },
  },
  {
    role: 'assistant',
    text: "**2 albums + 1 option** — that's everything. Your Recording Agreement is ready for review.",
  },
];
